import DatePickerInput from 'components/DateInput'
import FormInput from 'components/Forms/form-input'
import Translation from 'components/Translation'
import InputValidations from 'components/Validations/Field'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { EMAIL_REGEX, WORD_SINGLE_LINE_REGEX, PHONE_REGEX } from 'types/common'
import { classNames } from 'utils/dom-helpers'
import { getTranslation } from 'utils/language-helper'
import ConfirmDialogModal from '../ConfirmDialog'
import { GeneralFormValues } from './types'

function GeneralForm({
    archiveClient,
    onFormSubmit
}: {
    archiveClient(id: string, first_name: string, last_name: string): void,
    onFormSubmit(): void
}) {
    const ctx = useAuth()
    const { preferred_languages } = useTenantContext()
    const router = useRouter()
    const {
        translations,
    } = useAppContext()
    const { attributes, setAttributes } = ctx.CreateClientModal
    const [prompt_archive_message, setRowPromptMessage] = useState('')
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        ctx.CreateClientModal.setAttributes({
            ...ctx.CreateClientModal.attributes,
            user_id: attributes?.user_id as string,
            first_name: attributes?.first_name as string,
            last_name: attributes?.last_name as string,
            email: attributes?.email as string,
            phone: attributes?.phone as string,
            date_of_birth: attributes?.date_of_birth as Date,
            notes: attributes?.notes as string,
            preferredLanguageId: getDefaultValueForLanguage(),
            street1: attributes?.street1 as string,
            state: attributes?.state as string,
            city: attributes?.city as string,
            zip: attributes?.zip as string,
            country: attributes?.country as string,
            timezone: attributes?.timezone as string,
            cognitoId: attributes?.cognito_id as string,
            imageUrl: attributes?.image_url as string
        })
    }, [])

    useEffect(() => {
        if (attributes?.id) {
            setValue('first_name', attributes.first_name as string)
            setValue('last_name', attributes.last_name as string)
            setValue('email', attributes.email as string)
            setValue('phone', attributes.phone as string)
            setValue('notes', attributes.notes as string)
            setValue('language', getDefaultValueForLanguage())
        }
    }, [attributes?.id])

    const getDefaultValueForLanguage = () => {
        let result = preferred_languages[0].id
        if (attributes?.preferred_language_id) {
            result =
                preferred_languages?.find(
                    (lan: Record<string, string>) =>
                        lan.id === attributes?.preferred_language_id
                )?.id || ''
        }
        return result
    }

    async function handleClose() {
        reset()
        router.replace({
            pathname: router.pathname,
            query: null,
        })
        ctx.CreateClientModal.close()
    }

    const onSubmit: SubmitHandler<GeneralFormValues> = async () => {
        onFormSubmit()
    }

    function handleArchiveCancel() {
        setRowPromptMessage('')
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target
        ctx.CreateClientModal.setAttributes({
            ...ctx.CreateClientModal.attributes,
            [name]: value,
            isDirty: true,
        })
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)}>
            <div className="pb-5 lg:flex">
                <fieldset className="lg:pb-0 lg:w-1/2 lg:pr-4">
                    <FormInput
                        label='first_name'
                        htmlFor="first-name"
                        fieldError={errors.first_name}
                        required
                    >
                        <input
                            type="text"
                            autoComplete="first_name"
                            {...register('first_name', {
                                required: "common.first_name_required",
                                maxLength: 50,
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.first_name'
                                }
                            })}
                            onChange={handleOnChange}
                        />
                    </FormInput>
                </fieldset>
                <fieldset className="lg:w-1/2 lg:pl-4">
                    <FormInput
                        label='last_name'
                        htmlFor="last-name"
                        fieldError={errors.last_name}
                        required
                    >
                        <input
                            type="text"
                            autoComplete="last_name"
                            {...register('last_name', {
                                required: "common.last_name_required",
                                maxLength: 50,
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.last_name'
                                }
                            })}
                            onChange={handleOnChange}
                        />
                    </FormInput>
                </fieldset>
            </div>
            <div className="pb-5 lg:flex">
                <fieldset className="lg:w-1/2 lg:pr-4">
                    <FormInput
                        label='email'
                        htmlFor="email-address"
                        fieldError={errors.email}
                        required
                    >
                        <input
                            type="text"
                            autoComplete="email"
                            {...register('email', {
                                required: "common.email_required",
                                pattern: {
                                    value: EMAIL_REGEX,
                                    message: 'invalid.pattern.email'
                                }
                            })}
                            onChange={handleOnChange}
                        />
                    </FormInput>
                </fieldset>
                <fieldset className="lg:w-1/2 lg:pl-4">
                    <FormInput
                        label='phone'
                        htmlFor="phone"
                        fieldError={errors.phone}
                    >
                        <input
                            type="text"
                            autoComplete="phone"
                            {...register('phone', {
                                pattern: {
                                    value: PHONE_REGEX,
                                    message: 'invalid.pattern.phone'
                                }
                            })}
                            onChange={handleOnChange}
                        />
                    </FormInput>
                </fieldset>
            </div>
            <div className="pb-5 lg:flex gap-8">
                <fieldset className="lg:pb-0 lg:w-1/2 datapicker-client">
                    <Translation
                        render_as="label"
                        htmlFor="date-of-birth"
                        className={classNames(
                            'block text-sm',
                            errors.date_of_birth?.type ? 'text-red-700' : ''
                        )}
                        content_key="date_of_birth"
                    />
                    <DatePickerInput
                        className="p-2  focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md calendar"
                        maxDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="DD/MM/YYYY"
                        selected={ctx.CreateClientModal.attributes?.date_of_birth
                            ? new Date(ctx.CreateClientModal.attributes?.date_of_birth as Date)
                            : null}
                        onChange={(date: Date | null) => {
                            ctx.CreateClientModal.setAttributes({
                                ...ctx.CreateClientModal.attributes,
                                date_of_birth: date as Date,
                                isDirty: true,
                            })
                        }}
                    />
                    <InputValidations
                        field_error={errors.date_of_birth}
                        pattern_key='common.valid_date_required'
                    />
                </fieldset>
                <fieldset className="lg:pb-0 lg:w-1/2">
                    <Translation
                        render_as="label"
                        htmlFor="language"
                        className={classNames('block text-sm')}
                        content_key="select_language"
                    />
                    <select
                        id="language"
                        className={classNames(
                            'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm rounded-md'
                        )}
                        {...register('language', {})}
                        onChange={(e) => {
                            ctx.CreateClientModal.setAttributes({
                                ...ctx.CreateClientModal.attributes,
                                preferredLanguageId: e.target.value,
                                isDirty: true,
                            })
                        }}
                    >
                        {preferred_languages?.map(
                            (item: Record<string, string>) => {
                                return (
                                    <option value={item.id} key={item.id}>
                                        {item.text}
                                    </option>
                                )
                            }
                        )}
                    </select>
                </fieldset>
            </div>
            <fieldset>
                <Translation
                    render_as="label"
                    htmlFor="notes"
                    className={classNames(
                        'block text-sm',
                        errors.notes?.type ? 'text-red-700' : ''
                    )}
                    content_key="notes"
                />
                <textarea
                    id="notes"
                    className={classNames(
                        'placeholder-gray-300 mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm rounded-md',
                        errors.notes?.type
                            ? 'border-red-300 bg-red-100'
                            : ''
                    )}
                    placeholder={getTranslation(
                        'private_notes_client',
                        translations
                    )}
                    {...register('notes', {
                        required: false,
                        maxLength: 2000,
                    })}
                    onChange={handleOnChange}
                />
                <InputValidations
                    field_error={errors.notes}
                />
            </fieldset>
            <div>
                <div
                    className={classNames(
                        'flex relative mt-4',
                        !attributes?.id
                            ? 'justify-end'
                            : 'justify-start'
                    )}
                >
                    <button
                        type="button"
                        className={classNames(
                            'bg-red-100 text-red-500 btn-gap text-sm rounded-lg',
                            !attributes?.id ? 'hidden' : ''
                        )}
                        onClick={() => {
                            setRowPromptMessage(
                                getTranslation(
                                    'archive_client',
                                    translations,
                                    {
                                        first_name:
                                            attributes?.first_name as string,
                                        last_name:
                                            attributes?.last_name as string,
                                    }
                                )
                            )
                        }}
                    >
                        <i className="feather-archive" />
                    </button>
                    <div
                        className={classNames(
                            'flex gap-3 ',
                            attributes?.id
                                ? 'absolute inset-y-0 right-0'
                                : ''
                        )}
                    >
                        <button
                            type="reset"
                            onClick={() => {
                                setAttributes({})
                                handleClose()
                            }}
                            className="border-gray-200 rounded-lg border text-sm btn-gap"
                        >
                            <Translation content_key="cancel" />
                        </button>
                        <button
                            type="submit"
                            className={classNames(
                                'group relative flex justify-center',
                                'border border-transparent',
                                'rounded-md text-white text-sm',
                                'focus:outline-none btn-gap',
                                !attributes?.isDirty
                                    ? 'bg-primary-light cursor-not-allowed'
                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light cursor-pointer'
                            )}
                            disabled={!attributes?.isDirty}
                        >
                            <Translation content_key="save" />
                        </button>
                    </div>
                </div>
            </div>
            <ConfirmDialogModal
                prompt_message={prompt_archive_message}
                onConfirm={() => {
                    archiveClient(attributes?.id as string, attributes?.first_name as string, attributes?.last_name as string)
                    handleClose()
                }}
                onCancel={handleArchiveCancel}
            />
        </form>
    )
}

export default GeneralForm
